import React from 'react';
import App from './App';
import reportWebVitals from './reportWebVitals';
import {createRoot} from 'react-dom/client';
import store from './store';
import {Provider} from 'mobx-react';
import * as Sentry from '@sentry/react';

const isProduction = process.env.REACT_APP_MODE === 'production';
if (isProduction) {
    Sentry.init({
        dsn: 'https://055628b3dd763e5d89cae5198f692d3d@sentry.grtcloud.net/2',
        environment: process.env.REACT_APP_MODE,
        //开发或者本地设置false
        //注意：将其设置为 enabled: false 并不会阻止 Sentry 仪表的所有开销。要完全禁用 Sentry，需要根据环境有条件地调用 `Sentry.init`。
        enabled: isProduction,
        integrations: [
            Sentry.browserTracingIntegration({
                enableLongTask: false,
                // 匹配所有 *.grtcloud.net 域名接口
                tracePropagationTargets: [/\.grtcloud\.net/]
            }),
            Sentry.replayIntegration()
        ],
        tracesSampler: (samplingContext) => {
            // 优先继承上游trace采样结果，保证链路完整
            if (samplingContext.parentSampled !== undefined) {
                return samplingContext.parentSampled ? 0.1 : 0.0;
            }
            if (samplingContext.transactionContext) {
                const transactionContext = samplingContext.transactionContext || {},
                 {name, op} = transactionContext;
                // 管理端首页
                if (name && name?.includes('/manage/statistics')) return 0.01;
                // 普通 API 请求
                if (op === 'http.client') return 0.02;
            }
            // 默认采样率
            return 0.03;
        },
        replaysSessionSampleRate: 0.0,
        replaysOnErrorSampleRate: 0.01,
        ignoreErrors: [
            /^@sentry-internal/,
            'top.GLOBALS',
            /^Illegal invocation/,
            'ResizeObserver loop limit exceeded',
            // 业务接口错误对象被 reject 后页面已 toast 提示，无未处理异常价值
            /^Object captured as promise rejection with keys: code, message/,
            // 用户浏览器插件注入的第三方域名请求失败，与业务无关
            /utq\.vvipquan\.com/,
            // 用户浏览器直播助手类插件注入的全局变量缺失
            /LIDNotify/,
            // 网络追踪层主动取消请求的中断信号
            /^signal is aborted without reason/,
            // axios 形态错误对象被 reject（键序 code, config, message...），页面已兜底提示
            /^Object captured as promise rejection with keys: code, config, message/,
            // 第三方请求库 XHR 包装对象被整体 reject（对象键为 XHR 常量），页面已兜底提示
            /^Object captured as promise rejection with keys: DONE, HEADERS_RECEIVED/,
            // 第三方请求库已知缺陷：responseType 为 arraybuffer 的下载类接口读取 responseText 报错，无法改库
            /^InvalidStateError: Failed to read the 'responseText' property from 'XMLHttpRequest'/,
            // 浏览器标准行为：切换视频源/销毁播放器时媒体加载被用户代理中断，非业务错误
            /^AbortError: The fetching process for the media resource was aborted/,
            // 浏览器自动播放策略：用户未与页面交互前禁止自动播放，属用户环境限制
            /^NotAllowedError: play\(\) failed because the user didn't interact/,
            // 切换课程/销毁播放器时 AbortController 主动取消飞行中的请求，非业务错误
            /^AbortError: The user aborted a request/,
            // 静态资源预载失败（网络/缓存/CDN 边缘），页面样式由正常加载链路兜底，无功能影响
            /^Unable to preload CSS for/,
            // Safari 对已取消请求的固定文案（同 Load failed 一族），无业务堆栈可排查
            /^TypeError: cancelled$/,
            // play()调用后立刻pause()，播放请求被中断，快速切换视频常见
            /The play\(\) request was interrupted by a call to pause\(\)/,
            // requestFullscreen 必须由用户点击手势触发，异步调用全屏会触发该报错
            /requestFullscreen\(\) failed because the API was not called from inside a short-lived user-generated event handler/,
            // 进入/退出全屏，DOM元素已销毁，调用fullscreen API失败
            /Failed to execute 'requestFullscreen'|Failed to execute 'exitFullscreen'/,
            // 媒体播放器状态非法，视频源切换过程中触发
            /InvalidStateError.*media/,
            // 当前浏览器环境不支持全屏API（部分移动端/内嵌webview）
            /NotSupportedError.*fullscreen/,
            // 播放器状态非法，视频源切换过程中触发
            /AbortError.*play\(\)/,
            // 全屏请求被拒绝，部分浏览器环境不支持全屏API（部分移动端/内嵌webview）
            /Fullscreen request denied/,
            // 播放器状态非法，视频源切换过程中触发
            /AbortError: The operation was aborted\./,
            // 播放器状态非法，视频源切换过程中触发
            /^The operation was aborted\./,
            // 播放器状态非法，视频源切换过程中触发
            /AbortError: The play\(\) request was interrupted because the containing page was frozen\./,
            // 西瓜播放器内部错误
            /TypeError: null is not an object \(evaluating 'g\.target'\)/
        ],
        denyUrls: [
            /^chrome:\/\//i,
            /^chrome-extension:\/\//i,
            /^moz-extension:\/\//i,
            /^safari-extension:\/\//i,
            /^safari-web-extension:\/\//i,
            /extensions\//i,
            /^file:\/\//i,
            /^resource:\/\//i,
            /^webpack-internal:/i,
            /^webpack:/i,
            /jsdelivr\.net/i,
            /unpkg\.com/i,
            /cdnjs\.cloudflare\.com/i
        ],
        beforeSend(event) {
            const frames = event.exception?.values?.[0]?.stacktrace?.frames || [];
            // 全部堆栈都来自node_modules，才过滤
            if (frames.length > 0 && frames.every(f => f.filename?.includes('node_modules'))) {
                return null;
            }
            return event;
        }
    });
}

// 1、从三方跳过来，存入local
// 2、直接打开如果没有，给默认值---直播
// 3、退出应该清除local
// 4、存入local主要是为了低代码平台中outRequest使用
if (window.name) {
    localStorage.setItem('BI', window.name);
}
const root = createRoot(document.getElementById('root'));

root.render(
    <Provider store={store}>
        <App/>
    </Provider>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
